import React from "react";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  BankOutlined,
  BarChartOutlined,
  BellOutlined,
  CalendarOutlined,
  CarryOutOutlined,
  CheckSquareOutlined,
  ClusterOutlined,
  DashboardOutlined,
  DollarOutlined,
  DotChartOutlined,
  EyeOutlined,
  FieldTimeOutlined,
  FormOutlined,
  FundOutlined,
  HomeOutlined,
  LineChartOutlined,
  PieChartOutlined,
  RetweetOutlined,
  SafetyOutlined,
  ScheduleOutlined,
  SettingOutlined,
  SwapOutlined,
  TagsOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";

// 選單與路由的單一定義來源：App.js 由此產生 Route，DefaultLayout 由此產生選單。
// 兩邊共用同一份 permission，才不會出現「選單藏起來但網址直接輸入仍進得去」的落差。
//
// 這裡刻意不放頁面元件 —— 元件對應表在 App.js。若在此 import 頁面元件，
// navigation.test.js 會連帶載入 echarts 與 axios，測試就跑不動了。
//
// 每個項目配一顆獨立圖示，側邊欄收合成圖示列後才分辨得出來。
export const NAV_GROUPS = [
  {
    title: "工作",
    items: [
      { key: "todos", path: "/todos", label: "待辦事項", icon: <CheckSquareOutlined />, permission: "work.todos" },
      { key: "routines", path: "/routines", label: "例行工作", icon: <FieldTimeOutlined />, permission: "work.todos" },
    ],
  },
  {
    // 庫存與報表分開：庫存是「還能賣什麼、要不要調整」的日常作業，報表是回頭看成效，
    // 兩者的使用時機不同。庫存擺前面因為它是每天要動的。
    title: "庫存",
    items: [
      { key: "inventory", path: "/inventory", label: "房型庫存查詢", icon: <CalendarOutlined />, permission: "inventory.view" },
      // 訂房組的日常作業清單，業務不需要 —— 故自成一個權限碼，不跟庫存查詢共用。
      // 沒有對應的獨立 API：內容是由庫存查詢那支 API 的回應在前端推導出來的，
      // 資料本身業務本來就看得到，這個碼控制的是「這份作業清單要不要出現在誰的選單」。
      { key: "rebalance", path: "/inventory/rebalance", label: "待調控清單", icon: <SwapOutlined />, permission: "inventory.rebalance" },
      { key: "exposure", path: "/inventory/exposure", label: "OTA 曝光分析", icon: <EyeOutlined />, permission: "inventory.exposure" },
    ],
  },
  {
    title: "報表",
    items: [
      { key: "ops-dashboard", path: "/report/ops-dashboard", label: "營運儀表板", icon: <FundOutlined />, permission: "report.ops" },
      { key: "sales", path: "/report/sales", label: "銷售統計", icon: <LineChartOutlined />, permission: "report.sales" },
      { key: "houserate", path: "/report/houserate", label: "住房統計", icon: <PieChartOutlined />, permission: "report.houserate" },
      { key: "roomtypes", path: "/report/roomtypes", label: "房型統計", icon: <BarChartOutlined />, permission: "report.roomtypes" },
      { key: "guests", path: "/report/guests", label: "住客統計", icon: <TeamOutlined />, permission: "report.guests" },
      { key: "repeat-guests", path: "/report/repeat-guests", label: "回頭客分析", icon: <RetweetOutlined />, permission: "report.repeat-guests" },
      { key: "channels", path: "/report/channels", label: "通路統計", icon: <ApartmentOutlined />, permission: "report.channels" },
      { key: "promotions", path: "/report/promotions", label: "銷售方案統計", icon: <TagsOutlined />, permission: "report.promotions" },
      { key: "revpar-comparison", path: "/report/revpar-comparison", label: "RevPAR 房型比較", icon: <DotChartOutlined />, permission: "report.revpar" },
      { key: "rate-calendar", path: "/report/rate-calendar", label: "年度平旺假日規劃", icon: <ScheduleOutlined />, permission: "report.rate-calendar" },
    ],
  },
  {
    title: "行情",
    items: [
      { key: "dashboard", path: "/dashboard", label: "儀錶板", icon: <DashboardOutlined />, permission: "pricing.dashboard" },
      { key: "hotels", path: "/hotels", label: "飯店管理", icon: <BankOutlined />, permission: "pricing.hotels" },
      { key: "roomcategories", path: "/roomcategories", label: "房型類型", icon: <AppstoreOutlined />, permission: "pricing.roomcategories" },
      { key: "pricingroomtypes", path: "/roomtypes", label: "房型", icon: <HomeOutlined />, permission: "pricing.roomtypes" },
    ],
  },
  {
    title: "設定",
    items: [
      { key: "notification", path: "/setting/notification", label: "通知", icon: <BellOutlined />, permission: "setting.mail" },
      { key: "users", path: "/setting/users", label: "帳號管理", icon: <UserOutlined />, permission: "setting.users" },
      { key: "departments", path: "/setting/departments", label: "部門", icon: <ClusterOutlined />, permission: "setting.users" },
      { key: "login-logs", path: "/setting/login-logs", label: "登入記錄", icon: <SafetyOutlined />, permission: "setting.login-logs" },
      { key: "channel-commission", path: "/setting/channel-commission", label: "通路佣金", icon: <DollarOutlined />, permission: "setting.commission" },
      { key: "holidays", path: "/setting/holidays", label: "假日設定", icon: <CarryOutOutlined />, permission: "setting.holidays" },
      { key: "system", path: "/setting/system", label: "系統設定", icon: <SettingOutlined />, permission: "setting.system" },
      { key: "surveycake-surveys", path: "/setting/surveycake", label: "SurveyCake 問卷", icon: <FormOutlined />, permission: "setting.system" },
    ],
  },
];

export const NAV_ITEMS = NAV_GROUPS.reduce(
  (items, group) => items.concat(group.items),
  []
);

export function hasPermission(permissions, code) {
  if (!permissions) return false;
  return permissions.includes("*") || permissions.includes(code);
}

export function visibleItems(permissions) {
  return NAV_ITEMS.filter((item) => hasPermission(permissions, item.permission));
}

export function visibleGroups(permissions) {
  return NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasPermission(permissions, item.permission)),
  })).filter((group) => group.items.length > 0);
}

// 登入後的落地頁依序找第一個有權限的：待辦 → 營運儀表板 → 房型庫存查詢。
// 第一階段把待辦頁做成每個人的每日起點 (「待我處理」的日期分組)，所以它排第一 -
// 這刻意反轉了先前「業務仍落在庫存頁」的決定，當時那個畫面還不存在。
// 都沒有時才退回取排序最前的可見項目。
export const LANDING_PREFERENCES = ["work.todos", "report.ops", "inventory.view"];

export function landingPath(permissions) {
  const items = visibleItems(permissions);
  const target =
    LANDING_PREFERENCES.map((code) => items.find((item) => item.permission === code)).find(Boolean) ||
    items[0];
  return target ? target.path : null;
}
